import { FC, useEffect, useMemo } from 'react';
import { useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import type { ApiPayloads } from 'types/api-payloads';
import type { GeologicIntervalValidationError } from 'types/api-responses';
import { validateGeologicIntervalAtom } from './atoms';

type ValidationBody =
  ApiPayloads['validateGeologicInterval']['payload']['validation'];

interface GeologicIntervalValidationsProps {
  basinName: string;
  intervalName: string;
  isOther: boolean;
  onResult: (errors: GeologicIntervalValidationError[]) => void;
}

// Headless probe — debounced POST /validations/geologic_intervals on
// basin + interval name changes. Renders nothing.
export const GeologicIntervalValidations: FC<
  GeologicIntervalValidationsProps
> = ({ basinName, intervalName, isOther, onResult }) => {
  const validateInterval = useSetAtom(validateGeologicIntervalAtom);

  const debouncedValidate = useMemo(
    () =>
      debounce((payload: ValidationBody) => {
        validateInterval({
          type: 'POST',
          url: '/validations/geologic_intervals',
          data: { validation: payload },
          onSuccess: (data) => onResult(data.errors),
          onError: () => onResult([]),
        });
      }, 400),
    [validateInterval, onResult]
  );

  useEffect(() => {
    const trimmedName = intervalName.trim();
    if (!basinName || !trimmedName) {
      debouncedValidate.cancel();
      onResult([]);
      return;
    }
    debouncedValidate({
      basinName,
      geologicInterval: trimmedName.toUpperCase(),
      ...(isOther ? { seedCanonicalDefaults: true } : {}),
    });
  }, [basinName, intervalName, isOther, debouncedValidate, onResult]);

  useEffect(() => () => debouncedValidate.cancel(), [debouncedValidate]);

  return null;
};
